import Vue from 'vue';
import Component from 'vue-class-component';
import {CalendarServices} from '@/core/services/calendar.services';
import {TOAST_TYPES} from '@/plugins/ed-toast/values/ed-toast.values';
import { Ref } from 'vue-property-decorator';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import EdEditDialog from '@/components/common/ed-edit-dialog/ed-edit-dialog.component.vue';
import EdInputModel from '@/models/ed-input.model';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';


@Component({
    name: 'ed-my-calendar',
    components: {
    }
  })
export default class EdMyCalendarComponent extends Vue {

    /* Data */
    public calendars: any = [];
    public password = EdInputModel('password',  '',  {required, minLength: minLength(6), maxLength: maxLength(8)});
    public caldavEndpoint: string = '';
    public loading: boolean = true

    @Ref('dataTable') readonly dataTable: any;

    /* Hooks */
    mounted() {
        this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
        this.loading = true;
        CalendarServices.getCalendarsAndConfig(true)
        .then((data) => {
            this.calendars = data.calendars;
            this.caldavEndpoint = data.config.caldavEndpoint;
            this.loadCalendarsInDataTable();
            this.loading = false;
        }).catch(() => {
            this.$EdToast.create({
                msg: `${this.$t('tools.myInfo.calendar.errorLoading')}`,
                type: TOAST_TYPES.DANGER,
                top: '3rem',
                timeout: 3000
            });
            this.loadCalendarsInDataTable();
            this.loading = false;
        })
    }

    beforeDestroy() {
        this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
    }

    get calendarPrivate() {
        if (this.calendars && this.calendars.length>0){
            return this.calendars.find(c => c.info.calendarType === 'PRIVATE');
        }else{
            return {
                info:{
                    username: '',
                    password: '',
                }
            };
        }
    }

    get otherCalendars() {
        if (this.calendars && this.calendars.length>0){
            return this.calendars.filter(c => c.info.calendarType !== 'PRIVATE');
        }else{
            return [];
        }
    }


    /* Computed */
    get headers() {
        return [
            {text: this.$t('tools.myInfo.calendar.cols.name'), value: 'info.name', sortable: false, width: '40%'},
            {
                text: this.$t('tools.myInfo.calendar.cols.type'),
                value: 'info.calendarType',
                sortable: false,
                width: '30%'
            },
            {
                text: this.$t('tools.myInfo.calendar.cols.access'),
                value: 'info.canEdit',
                sortable: false,
                width: '30%'
            }
        ];
    }

    async updateCalendar() {
        if (this.validatePass()){
            this.loading = true;
            let body = { 'calendarId': this.calendarPrivate.info.calendarId,  'caldavPassword': this.password.value }
            CalendarServices.updateCalendar(body).then(()=> {
                return CalendarServices.getCalendarsAndConfig(true);
            }).then((data) => {
                this.calendars = data.calendars;
                this.caldavEndpoint = data.config.caldavEndpoint;
                this.loading = false;
            }).catch((e) => {
                const msg = e.message;
                this.$EdToast.create({
                    msg:  e?.data?.message,
                    type: TOAST_TYPES.DANGER,
                    top: '3rem',
                    timeout: 3000
                });
                this.loading = false;
            });
        }
    }

    get rules() {
        return [v => !!v || this.$t('errors.required'),
                v =>  (v && v.length <= 8 ) || this.$t('errors.maxLengthData', {max: 8}),
                v =>  (v && v.length >= 6 ) || this.$t('errors.minLengthData', {min:6})];
    }

    public validatePass(){
        let error;
        if (!this.password.value){
            error = this.$t('errors.required');
        }else if (this.password.value.length > 8){
            error = this.$t('errors.maxLengthData', {max: 8});
        }else if (this.password.value.length < 6){
            error = this.$t('errors.minLengthData', {min: 6});
        }

        if(error){
            this.$EdToast.create({
                msg: `${error}`,
                type: TOAST_TYPES.DANGER,
                top: '3rem',
                timeout: 3000
            });
            return false;
        }
        return true;
    }

    public copyClipboard(value){
        if (value == 'username'){
            navigator.clipboard.writeText(this.calendarPrivate.info.username);
        }else if (value == 'password'){
            navigator.clipboard.writeText(this.calendarPrivate.info.password);
        }else if (value == 'url'){
            navigator.clipboard.writeText(this.caldavEndpoint);
        }
    }


    public capitalize(word:string){
        const firstLetter = word.charAt(0);
        const firstLetterCap = firstLetter.toUpperCase();
        const remainingLetters = word.slice(1);
        return firstLetterCap + remainingLetters.toLowerCase();
    }

    private loadCalendarsInDataTable(): void {

        if (!this.dataTable || !this.dataTable.loadData) {
            return;
        }

        if (!this.calendars || this.calendars.length == 0) {
            this.dataTable.loadData({ items: [], headers: this.headers });
            return;
        }

        const slots = {
            'item.info.calendarType': {
                template: document.querySelector('#item\\.info\\.calendarType').innerHTML,
                templateParams: [{ key: 'slotData.item.info.calendarType', value: (params) => params.parent.capitalize(params.item.info.calendarType) }],
            },
            'item.info.canEdit': {
                template: document.querySelector('#item\\.info\\.canEdit').innerHTML,
                templateParams: [{
                    key: 'tools.myInfo.calendar.access',
                    value: (params) => !params.slotData.item.info.canEdit ?
                        this.$t('tools.myInfo.calendar.accessRead')
                        : this.$t('tools.myInfo.calendar.accessWrite')
                }],
            },
            parent: this,
        };

        this.dataTable.loadData({ items: this.otherCalendars, headers: this.headers, slots: slots });
    }

    private _onLanguageChanged(lang: string): void {
        this.$nextTick(() => {
            this.dataTable?.setAttribute('lang', lang);
            this.loadCalendarsInDataTable();
        });
    }

    public updatePasswordPopup() {
        this.$EdModal.createModal(EdEditDialog, {
            inputText: this.password.value, onChangeCallback: async (newPassword: string) => {
                this.password.value = newPassword;
                await this.updateCalendar();
            },
            minLength: 6,
            maxLength: 8,
            showRangeLength: true,
            required: true
        });
    }
}
